import React from 'react';
import { useTranslation } from '../LanguageContext';
import Logo from './Logo';
import { trackEvent } from '../stats';
import ReactCountryFlag from 'react-country-flag';
import { isVariantB } from '../utils/abVariant';

const REGISTRATION_OPEN = true; // muuda true kui maksed valmis


const FLAGS = [
  { code: 'en', country: 'GB', name: 'English' },
  { code: 'cs', country: 'CZ', name: 'Čeština' },
  { code: 'da', country: 'DK', name: 'Dansk' },
  { code: 'de', country: 'DE', name: 'Deutsch' },
  { code: 'es', country: 'ES', name: 'Español' },
  { code: 'et', country: 'EE', name: 'Eesti' },
  { code: 'fi', country: 'FI', name: 'Suomi' },
  { code: 'fr', country: 'FR', name: 'Français' },
  { code: 'hu', country: 'HU', name: 'Magyar' },
  { code: 'it', country: 'IT', name: 'Italiano' },
  { code: 'lt', country: 'LT', name: 'Lietuvių' },
  { code: 'lv', country: 'LV', name: 'Latviešu' },
  { code: 'nl', country: 'NL', name: 'Nederlands' },
  { code: 'no', country: 'NO', name: 'Norsk' },
  { code: 'pl', country: 'PL', name: 'Polski' },
  { code: 'pt', country: 'PT', name: 'Português' },
  { code: 'ro', country: 'RO', name: 'Română' },
  { code: 'ru', country: 'RU', name: 'Русский' },
  { code: 'sk', country: 'SK', name: 'Slovenčina' },
  { code: 'sv', country: 'SE', name: 'Svenska' },
  { code: 'el', country: 'GR', name: 'Ελληνικά' },
  { code: 'tl', country: 'PH', name: 'Filipino' },
];

function Landing({ onRegister, onDemo, onReferences }) {
  const { t, lang, changeLang } = useTranslation();
  const [langDropdownOpen, setLangDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  // Tracking-state: viimati nähtud sektsioon (landing_exit jaoks)
  const lastVisibleSectionRef = React.useRef('hero');
  const pageStartTimeRef = React.useRef(Date.now());

  React.useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setLangDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ═══ TRACKING: scroll % milestones, time checkpoints, beforeunload ═══
  React.useEffect(() => {
    const scrolledPercents = new Set(); // 25, 50, 75, 100 — fire-once per session

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);
      [25, 50, 75, 100].forEach(milestone => {
        if (pct >= milestone && !scrolledPercents.has(milestone)) {
          scrolledPercents.add(milestone);
          trackEvent(`landing_scroll_${milestone}`);
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Time checkpoints
    const timeouts = [10, 30, 60, 120, 300].map(sec =>
      setTimeout(() => trackEvent(`landing_time_${sec}s`), sec * 1000)
    );

    // beforeunload — landing_exit
    const onUnload = () => {
      const totalMs = Date.now() - pageStartTimeRef.current;
      trackEvent('landing_exit', {
        value: lastVisibleSectionRef.current,
        duration_ms: totalMs,
      });
    };
    window.addEventListener('beforeunload', onUnload);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', onUnload);
      timeouts.forEach(clearTimeout);
    };
  }, []);

  // ═══ TRACKING: IntersectionObserver — section visibility ═══
  // Sektsioon "viewed" = >=50% sektsioonist ekraanil >=1 sekund. Fire-once per session.
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return; // vana brauser
    const seen = new Set();
    const timers = new Map();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const name = entry.target.dataset.section;
        if (!name || seen.has(name)) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Käivita 1s timer; tühista kui rato langeb alla
          if (!timers.has(name)) {
            const tid = setTimeout(() => {
              if (!seen.has(name)) {
                seen.add(name);
                lastVisibleSectionRef.current = name;
                trackEvent('landing_section_viewed', { value: name });
              }
              timers.delete(name);
            }, 1000);
            timers.set(name, tid);
          }
        } else {
          // välja kerritud enne 1s — tühista timer
          if (timers.has(name)) {
            clearTimeout(timers.get(name));
            timers.delete(name);
          }
        }
      });
    }, { threshold: [0.5] });

    // Vaatame kõik elemendid, millel on data-section atribuut
    const els = document.querySelectorAll('[data-section]');
    els.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const currentFlag = FLAGS.find(f => f.code === lang) || FLAGS[0];

  const handleStart = () => {
    trackEvent('cta_click', { value: 'alusta' });
    if (!REGISTRATION_OPEN) { alert(t('landing.comingSoon')); return; }
    onRegister();
  };

  // A/B: variant B (utm_content=ad2) = kerge hero, demo-nupp esimesel ekraanil
  const variantB = isVariantB();

  // Teaduslik alus + viidete link — variant B-s liigub hero alt allapoole
  const thanksLine = (
    <p className="landing-thanks" style={{
      margin: variantB ? '16px 0 0' : '8px 0 0',
      fontSize: 'clamp(11px, 1.3vw, 15px)',
      color: '#f0d060',
      fontStyle: 'italic',
      lineHeight: '1.6',
      textAlign: variantB ? 'center' : 'right',
      opacity: 0.85,
    }}>
      {t('landing.thanks')}{' '}
      <button
        type="button"
        onClick={onReferences}
        style={{
          background: 'none', border: 'none', padding: 0,
          color: '#4ade80', textDecoration: 'underline', cursor: 'pointer',
          fontStyle: 'italic', fontSize: 'inherit', fontFamily: 'inherit',
        }}
      >
        {t('landing.referencesLink')}
      </button>
    </p>
  );

  return (
    <div style={{ width: '100%', color: '#fff', fontFamily: "'Segoe UI', Arial, sans-serif", boxSizing: 'border-box', overflowX: 'hidden' }}>

      {/* ══ PÄIS: ainult parempoolne keelenupp (lipurida eemaldatud 02.05.2026) ══ */}
      <div style={{ padding: '0 14px 0 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: '36px' }}>
          {/* Paremal: keelenupp dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '6px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#fff',
                whiteSpace: 'nowrap',
              }}
            >
              <ReactCountryFlag countryCode={currentFlag.country} svg style={{ width: '18px', height: '13px', display: 'block', borderRadius: '2px' }} />
              <span>{currentFlag.name}</span>
              <span style={{ fontSize: '10px' }}>{langDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {langDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '36px',
                right: 0,
                background: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                overflow: 'hidden',
                minWidth: '160px',
                maxHeight: '50vh',
                overflowY: 'auto',
                zIndex: 1000,
              }}>
                {FLAGS.map(f => (
                  <div
                    key={f.code}
                    onClick={() => { changeLang(f.code); setLangDropdownOpen(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 14px',
                      cursor: 'pointer',
                      background: f.code === lang ? 'rgba(192,132,252,0.15)' : 'transparent',
                      borderLeft: f.code === lang ? '3px solid #c084fc' : '3px solid transparent',
                      fontSize: '13px',
                      color: '#fff',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(192,132,252,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.background = f.code === lang ? 'rgba(192,132,252,0.15)' : 'transparent'}
                  >
                    <ReactCountryFlag countryCode={f.country} svg style={{ width: '20px', height: '14px', display: 'block', borderRadius: '2px' }} />
                    <span>{f.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Teaduslik alus + viidete link — variant B-s renderdub hero järel */}
        {!variantB && thanksLine}
      </div>

      {/* ══ SISU ══ */}
      <div className="landing-content-area" style={{ maxWidth: '600px', margin: '0 auto', padding: 'clamp(8px, 2vw, 32px) 16px 40px' }}>

        {/* Logo + Hero — kompaktne: logo + slogan KÕRVUTI ÜHES rohelises kastis.
            Logo.js text-plokk käitab seda layout'i (showTagline=true puhul row-flex). */}
        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <div data-section="hero" style={{ marginBottom: '10px' }}>
            <Logo size={window.innerWidth < 500 ? 23 : 35} showTagline={true} light={false} />
          </div>
          {variantB ? (
            <>
              {/* ═══ VARIANT B HERO (utm_content=ad2): pealkiri + üks alalause +
                  demo-nupp KOHE — eesmärk: nupp nähtav FB in-app esimesel ekraanil (~380px).
                  Alalaused/tease liiguvad nupu alla koomale, thanksLine hero lõppu. ═══ */}
              <h1 style={{
                fontSize: 'clamp(22px, 5.5vw, 34px)',
                fontWeight: '800',
                lineHeight: '1.25',
                margin: '0 0 12px',
                color: '#fff',
              }}>
                {t('landingB.h1')}
              </h1>
              <p style={{
                fontSize: 'clamp(15px, 2vw, 18px)',
                color: 'rgba(255,255,255,0.92)',
                lineHeight: '1.5',
                margin: '0 0 16px',
                fontWeight: '500',
              }}>
                {t('landingB.sub')}
              </p>
              <div data-section="demo_button" style={{ marginBottom: '10px', textAlign: 'center' }}>
                <button
                  className="landing-cta-btn"
                  onClick={() => { trackEvent('cta_click', { value: 'demo' }); onDemo(); }}
                  style={{
                    background: '#22c55e', color: '#0a1f0d',
                    border: 'none', borderRadius: '14px',
                    padding: '16px 20px', cursor: 'pointer',
                    boxShadow: '0 6px 22px rgba(34,197,94,0.5)',
                    width: '100%', maxWidth: '460px',
                    fontSize: 'clamp(17px, 4.5vw, 22px)', fontWeight: '800',
                    lineHeight: '1.2', whiteSpace: 'normal',
                  }}
                >
                  {t('landingB.demoBtn')}
                </button>
              </div>
              {/* Kompaktselt nupu all: senised alalaused ühe reana
                  (demoTease eemaldatud — kordas nupu teksti) */}
              <p style={{
                fontSize: 'clamp(13px, 1.7vw, 15px)',
                color: 'rgba(255,255,255,0.75)',
                lineHeight: '1.5',
                margin: '0 0 14px',
              }}>
                {t('landing.heroSubA')} {t('landing.heroSubB')}
              </p>
            </>
          ) : (
            <>
              <h1 style={{
                fontSize: 'clamp(22px, 5vw, 38px)',
                fontWeight: '800',
                lineHeight: '1.25',
                margin: '0 0 14px',
                color: '#fff',
              }}>
                <span style={{ display: 'block' }}>{t('landing.h1Line1')}</span>
                <span style={{ display: 'block', marginTop: '6px' }}>
                  {/* Splitime **word** markeri — h1 ümbritsev fontWeight on 800, strong peab olema 900 et erineda */}
                  {t('landing.h1Line2').split('**').map((part, i) =>
                    i % 2 === 1
                      ? <strong key={i} style={{ fontWeight: 900 }}>{part}</strong>
                      : <React.Fragment key={i}>{part}</React.Fragment>
                  )}
                </span>
              </h1>
              <p style={{
                fontSize: 'clamp(14px, 1.9vw, 17px)',
                color: 'rgba(255,255,255,0.92)',
                lineHeight: '1.5',
                margin: '0 0 4px',
                fontWeight: '500',
              }}>
                {t('landing.heroSubA')}
              </p>
              <p style={{
                fontSize: 'clamp(14px, 1.9vw, 17px)',
                color: 'rgba(255,255,255,0.92)',
                lineHeight: '1.5',
                margin: '0 0 14px',
                fontWeight: '500',
              }}>
                {t('landing.heroSubB')}
              </p>

              {/* ═══ DEMO PLOKK — TÕSTETUD esimesele ekraanile (oli SOLO ees, hero JÄREL).
                  Eesmärk: anda mobiili esimesel ekraanil ühtegi tegevus-CTA. ═══ */}
              <div data-section="demo_button" style={{
                background: 'rgba(74,222,128,0.06)',
                border: '1px solid rgba(74,222,128,0.18)',
                borderRadius: '16px',
                padding: 'clamp(12px, 2.2vw, 18px)',
                marginBottom: '14px',
                textAlign: 'center',
              }}>
                {/* Demo CTA-eelne info — VALGE tekst + roheline animeeritud ↓ */}
                <p style={{
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 'clamp(15px, 1.8vw, 18px)',
                  fontWeight: '500',
                  lineHeight: '1.55',
                  margin: '0 0 12px',
                  textAlign: 'center',
                }}>
                  {t('landing.demoTease')}{' '}
                  <span style={{
                    display: 'inline-block',
                    color: '#22c55e',
                    fontSize: 'clamp(20px, 2.6vw, 26px)',
                    fontWeight: '700',
                    verticalAlign: 'middle',
                    animation: 'arrowBounce 1.6s ease-in-out infinite',
                  }}>
                    ↓
                  </span>
                </p>
                {/* Demo nupp: täidetud roheline, MUST tekst, 👀 vasak + tekst keskel + 🎯 parem */}
                <button
                  className="landing-cta-btn"
                  onClick={() => { trackEvent('cta_click', { value: 'demo' }); onDemo(); }}
                  style={{
                    background: '#22c55e', color: '#0a1f0d',
                    border: 'none', borderRadius: '14px',
                    padding: '14px 14px', cursor: 'pointer',
                    boxShadow: '0 6px 22px rgba(34,197,94,0.5)',
                    width: '100%', maxWidth: '460px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontSize: 'clamp(20px, 3vw, 26px)', flexShrink: 0, lineHeight: 1 }}>👀</span>
                  <span style={{
                    flex: 1, textAlign: 'center',
                    fontSize: 'clamp(17px, 2.5vw, 22px)', fontWeight: '800',
                    lineHeight: '1.2', whiteSpace: 'normal', color: '#0a1f0d',
                  }}>
                    {t('landingCta.demoBtn')}
                  </span>
                  <span style={{ fontSize: 'clamp(20px, 3vw, 26px)', flexShrink: 0, lineHeight: 1 }}>🎯</span>
                </button>
              </div>
            </>
          )}

          {/* Roosa hoiatuslause — bold + kursiivis. Liigutatud DEMO nupu JÄRELE
              (eelnevalt oli pärast 3 emoji-rida) — valmistab ette demo OOTUST. */}
          <p data-section="pink_intro" style={{
            margin: '16px 0 16px',
            color: '#fb7fba',
            fontSize: 'clamp(16px, 2vw, 19px)',
            fontWeight: '700',
            lineHeight: '1.55',
            textAlign: 'center',
            letterSpacing: '0.02em',
          }}>
            {t('landing.heroWarning')}
          </p>

          {/* Variant B: teaduslik alus + viidete link siin (hero ülaosast liigutatud) */}
          {variantB && thanksLine}

        </div>

        {/* PLOKK 1: Privaatsus rohelises raamis — RAHULIK ja USUTAV ümberkujundus.
            Eemaldatud: 🔒 lukk, 3 punast hüüumärki (paistsid hoiatusena).
            Lisatud: 🛡️ kilp pealkirjas, ✓ rohelised linnukesed loetelule, 📅 andmesäilivuse ikoon. */}
        <div data-section="privacy" style={{
          background: 'rgba(34,197,94,0.07)',
          border: '1.5px solid rgba(74,222,128,0.4)',
          borderRadius: '14px',
          padding: 'clamp(14px, 2.5vw, 20px)',
          margin: '0 0 10px',
        }}>
          {/* Pealkiri — 🛡️ kilp + tekst */}
          <p style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4em',
            color: '#f0d060',
            fontSize: 'clamp(17px, 2.2vw, 20px)',
            fontWeight: '700',
            lineHeight: '1.3',
            margin: '0 0 10px',
          }}>
            <span style={{ flexShrink: 0 }}>🛡️</span>
            <span>{t('landing.privacyTitle')}</span>
          </p>

          {/* Sissejuhatav lause */}
          <p style={{
            color: '#f0d060',
            fontSize: 'clamp(15px, 1.8vw, 17px)',
            lineHeight: '1.55',
            margin: '0 0 10px',
            fontWeight: '500',
          }}>
            {t('landing.privacyIntro')}
          </p>

          {/* 3 ✓ linnukestega rida — hanging indent (sama flex-loogika nagu hero emoji-ridadel) */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '10px',
          }}>
            {[
              t('landing.privacySolo'),
              t('landing.privacyPair'),
              t('landing.privacyFriend'),
            ].map((line, i) => (
              <p key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5em',
                color: '#f0d060',
                fontSize: 'clamp(14px, 1.7vw, 16px)',
                lineHeight: '1.5',
                margin: 0,
                fontWeight: '500',
              }}>
                <span style={{ flexShrink: 0, color: '#4ade80', fontWeight: 700 }}>✓</span>
                <span>{line}</span>
              </p>
            ))}
          </div>

          {/* Outro lause */}
          <p style={{
            color: '#f0d060',
            fontSize: 'clamp(15px, 1.8vw, 17px)',
            lineHeight: '1.55',
            margin: 0,
            fontWeight: '500',
          }}>
            {t('landing.privacyOutro')}
          </p>

          {/* 📅 andmesäilivuse rida — väiksem font, eraldatud peene ülemise joonega */}
          <p style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5em',
            color: '#f0d060',
            fontSize: 'clamp(12px, 1.4vw, 14px)',
            lineHeight: '1.55',
            margin: '12px 0 0',
            paddingTop: '10px',
            borderTop: '1px solid rgba(74,222,128,0.2)',
            fontWeight: '500',
            opacity: 0.9,
          }}>
            <span style={{ flexShrink: 0 }}>📅</span>
            <span>{t('landing.privacyRetention')}</span>
          </p>
        </div>

        {/* ═══ SOLO NUPP — suur kuldne, brändivärv. data-section="buttons_group"
            märgib alguse 3 nupu plokile (Solo + PAIR + Soovita). ═══ */}
        <button
          data-section="buttons_group"
          className="landing-cta-btn"
          onClick={() => { trackEvent('cta_click', { value: 'solo_full' }); handleStart(); }}
          style={{
            background: 'linear-gradient(135deg, #f5d08a 0%, #d97706 100%)',
            color: '#1a0035', border: 'none', borderRadius: '16px',
            padding: '18px 20px', cursor: 'pointer',
            boxShadow: '0 6px 24px rgba(217,119,6,0.45)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            width: '100%', maxWidth: '480px',
            margin: '0 auto 12px',
          }}
        >
          <span style={{
            fontSize: 'clamp(16px, 2.2vw, 20px)', fontWeight: '800',
            letterSpacing: '0.5px', lineHeight: '1.25',
          }}>
            {t('landingCta.soloBigBtn')}
          </span>
          {/* 3 linnukest — wrap kui mobiilis ei mahu, säilib brand-värv (#1a0035 tume lilla) */}
          <span style={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
            columnGap: '10px', rowGap: '2px',
            fontSize: 'clamp(11px, 1.3vw, 13px)', fontWeight: '700',
            color: '#1a0035', opacity: 0.92, lineHeight: '1.3',
          }}>
            <span>✓ {t('landing.soloCheck1')}</span>
            <span>✓ {t('landing.soloCheck2')}</span>
            <span>✓ {t('landing.soloCheck3')}</span>
          </span>
          <span style={{
            fontSize: 'clamp(12px, 1.4vw, 14px)', fontWeight: '600',
            opacity: 0.78, color: '#1a0035',
          }}>
            {t('landingCta.fullTestBtnLine2')}
          </span>
        </button>

        {/* ═══ PAARI NUPP — väiksem (~70% solo'st), roosa täidetud ═══ */}
        <button
          className="landing-cta-btn"
          onClick={() => { trackEvent('cta_click', { value: 'pair' }); handleStart(); }}
          style={{
            background: 'linear-gradient(135deg, #e91e8c, #c2185b)',
            color: '#fff', border: 'none', borderRadius: '14px',
            padding: '13px 18px', cursor: 'pointer',
            boxShadow: '0 4px 18px rgba(233,30,140,0.4)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
            width: '100%', maxWidth: '340px',
            margin: '0 auto 18px',
          }}
        >
          <span style={{
            fontSize: 'clamp(14px, 1.8vw, 16px)', fontWeight: '700', lineHeight: '1.3',
          }}>
            {t('landingCta.pairBigBtn')}
          </span>
          <span style={{
            fontSize: 'clamp(11px, 1.2vw, 13px)', fontWeight: '500', opacity: 0.9,
          }}>
            {t('landingCta.fullTestBtnLine2')}
          </span>
        </button>

        {/* ═══ SOOVITA SÕBRALE NUPP — oranž, sekundaarne CTA, ~70% PAIR-i suurusest ═══ */}
        <button
          className="landing-cta-btn"
          onClick={() => {
            trackEvent('share_clicked', { value: 'landing' });
            // BUG-FIX v2: kasuta lühem shareText (~50 chars) AINULT preview kärbete vältimiseks.
            // Title 'Inoroff' (mitte INOROFF) — sõbralikum brand. URL eraldi parameetrina
            // näitab klikitavat link preview kaardil.
            const msg = t('share.landing.shareText');
            if (navigator.share) navigator.share({
              title: 'Inoroff',
              text: msg,
              url: 'https://inoroff.org',
            }).catch(() => {});
            else navigator.clipboard.writeText(msg + ' https://inoroff.org').catch(() => {});
          }}
          style={{
            background: '#f97316', color: '#fff',
            border: 'none', borderRadius: '14px',
            padding: '11px 18px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(249,115,22,0.35)',
            lineHeight: '1.3',
            width: '100%', maxWidth: '300px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
            margin: '6px auto 22px',
          }}
        >
          <span style={{ fontSize: 'clamp(13px, 1.5vw, 15px)', fontWeight: '700' }}>
            {t('share.landing.button')}
          </span>
          <span style={{
            fontSize: 'clamp(13px, 1.4vw, 14px)', fontWeight: '500',
            fontStyle: 'italic', opacity: 1.0, lineHeight: '1.3',
          }}>
            {t('landing.recommendFriendSubtext')}
          </span>
        </button>

        {/* ═══ AUS KOKKULEPE — roheline raam (sama stiil kui privaatsuse kast), 🤝 pealkiri,
            3 ✓ linnukest, kursiivne intro+closing, eraldusjoon enne 💡 lõppu ═══ */}
        <div data-section="aus_kokkulepe" style={{
          background: 'rgba(34,197,94,0.07)',
          border: '1.5px solid rgba(74,222,128,0.4)',
          borderRadius: '14px',
          padding: 'clamp(14px, 2.5vw, 20px)',
          marginBottom: '22px',
        }}>
          {/* Pealkiri — 🤝 + tekst */}
          <p style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4em',
            color: '#f0d060',
            fontSize: 'clamp(17px, 2.2vw, 20px)',
            fontWeight: '700',
            lineHeight: '1.3',
            margin: '0 0 10px',
          }}>
            <span style={{ flexShrink: 0 }}>🤝</span>
            <span>{t('landing.fairDealTitle')}</span>
          </p>

          {/* Sissejuhatav rida — kursiivis, väiksem font */}
          <p style={{
            color: '#f0d060',
            fontSize: 'clamp(14px, 1.7vw, 16px)',
            fontStyle: 'italic',
            lineHeight: '1.55',
            margin: '0 0 12px',
            fontWeight: '500',
            opacity: 0.92,
          }}>
            {t('landing.fairDealIntro')}
          </p>

          {/* 3 ✓ linnukestega rida — hanging indent (sama loogika kui privacy kast) */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '12px',
          }}>
            {[
              t('landing.fairDealItem1'),
              t('landing.fairDealItem2'),
              t('landing.fairDealItem3'),
            ].map((line, i) => (
              <p key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5em',
                color: '#f0d060',
                fontSize: 'clamp(14px, 1.7vw, 16px)',
                lineHeight: '1.5',
                margin: 0,
                fontWeight: '600',
              }}>
                <span style={{ flexShrink: 0, color: '#4ade80', fontWeight: 700 }}>✓</span>
                <span>{line}</span>
              </p>
            ))}
          </div>

          {/* 💡 lõpurida — kursiivis, eraldusjoonega ülal (sama stiil kui privacy retention) */}
          <p style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5em',
            color: '#f0d060',
            fontSize: 'clamp(13px, 1.6vw, 15px)',
            fontStyle: 'italic',
            lineHeight: '1.55',
            margin: '12px 0 0',
            paddingTop: '10px',
            borderTop: '1px solid rgba(74,222,128,0.2)',
            fontWeight: '500',
          }}>
            <span style={{ flexShrink: 0 }}>💡</span>
            <span>{t('landing.fairDealClosing')}</span>
          </p>
        </div>



        {/* ═══ REFLEKTSIOONI PLOKK — pärast hinna/aususe plokki ═══ */}
        <div data-section="reflection" style={{
          background: 'rgba(192,132,252,0.08)',
          border: '1.5px solid rgba(192,132,252,0.3)',
          borderRadius: '16px',
          padding: 'clamp(20px, 3.5vw, 28px)',
          marginBottom: '22px',
        }}>
          <p style={{
            color: '#f0d060', fontSize: 'clamp(17px, 2.2vw, 22px)',
            fontWeight: '700', lineHeight: '1.4',
            margin: '0 0 14px', textAlign: 'center',
          }}>
            {t('landingCta.reflectionTitle')}
          </p>
          <p style={{
            color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(14px, 1.7vw, 17px)',
            fontWeight: '500', lineHeight: '1.55',
            margin: '0 0 16px', textAlign: 'center',
          }}>
            {t('landingCta.reflectionSubtitle')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {[
              t('landingCta.reflectionPoint1'),
              t('landingCta.reflectionPoint2'),
              t('landingCta.reflectionPoint3'),
            ].map((line, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '18px', flexShrink: 0, lineHeight: 1.3 }}>✨</span>
                <p style={{
                  margin: 0, color: '#fff',
                  fontSize: 'clamp(14px, 1.6vw, 16px)', fontWeight: '500',
                  lineHeight: '1.55',
                }}>
                  {line}
                </p>
              </div>
            ))}
          </div>
          <p style={{
            color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(13px, 1.4vw, 15px)',
            fontStyle: 'italic', lineHeight: '1.5',
            margin: 0, textAlign: 'center',
          }}>
            {t('landingCta.reflectionFooter')}
          </p>
        </div>

        {/* ═══ KUIDAS INOROFFI SAAD KASUTADA — 3 alaplokki, igaüks oma värviga raam ═══ */}
        <div data-section="kuidas_kasutada" style={{ marginBottom: '24px' }}>
          <h2 style={{
            color: '#fff', fontSize: 'clamp(18px, 2.4vw, 24px)',
            fontWeight: '800', letterSpacing: '0.5px',
            margin: '0 0 16px', textAlign: 'center',
            textTransform: 'uppercase',
          }}>
            {t('landingCta.usageTitle')}
          </h2>

          {/* Alaplokk 1 — kuldne (sama värv mis solo nupp) */}
          <div style={{
            background: 'rgba(217,119,6,0.10)',
            border: '1.5px solid rgba(245,208,138,0.5)',
            borderRadius: '14px',
            padding: 'clamp(14px, 2.5vw, 20px)',
            marginBottom: '10px',
          }}>
            <p style={{
              color: '#f5d08a', fontSize: 'clamp(15px, 1.9vw, 18px)',
              fontWeight: '700', lineHeight: '1.4',
              margin: '0 0 8px',
            }}>
              👤 {t('landingCta.usageSoloTitle')}
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(14px, 1.6vw, 16px)',
              lineHeight: '1.6', margin: 0,
            }}>
              {t('landingCta.usageSoloDesc')}
            </p>
          </div>

          {/* Alaplokk 2 — roosa (sama värv mis paari nupp) */}
          <div style={{
            background: 'rgba(233,30,140,0.08)',
            border: '1.5px solid rgba(233,30,140,0.4)',
            borderRadius: '14px',
            padding: 'clamp(14px, 2.5vw, 20px)',
            marginBottom: '10px',
          }}>
            <p style={{
              color: '#f8a5cf', fontSize: 'clamp(15px, 1.9vw, 18px)',
              fontWeight: '700', lineHeight: '1.4',
              margin: '0 0 8px',
            }}>
              👫 {t('landingCta.usagePairTitle')}
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(14px, 1.6vw, 16px)',
              lineHeight: '1.6', margin: 0,
            }}>
              {t('landingCta.usagePairDesc')}
            </p>
          </div>

          {/* Alaplokk 3 — oranž */}
          <div style={{
            background: 'rgba(249,115,22,0.08)',
            border: '1.5px solid rgba(249,115,22,0.4)',
            borderRadius: '14px',
            padding: 'clamp(14px, 2.5vw, 20px)',
          }}>
            <p style={{
              color: '#fdba74', fontSize: 'clamp(15px, 1.9vw, 18px)',
              fontWeight: '700', lineHeight: '1.4',
              margin: '0 0 8px',
            }}>
              🧑‍🤝‍🧑 {t('landingCta.usageFriendTitle')}
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(14px, 1.6vw, 16px)',
              lineHeight: '1.6', margin: 0,
            }}>
              {t('landingCta.usageFriendDesc')}
            </p>
          </div>
        </div>

        {/* Inclusivity */}
        <p style={{
          fontSize: 'clamp(15px, 1.7vw, 18px)',
          color: '#fff',
          lineHeight: '1.75',
          textAlign: 'center',
          margin: '0 0 24px',
          fontWeight: '500',
        }}>
          {t('landingCta.inclusivity')}
        </p>

        {/* FAQ */}
        <button
          data-section="faq"
          onClick={() => window.dispatchEvent(new CustomEvent('openFaq'))}
          style={{
            width: '100%', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
            padding: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
          }}
        >
          {t('landing.faqBtn')}
        </button>

      </div>

    </div>
  );
}

export default Landing;
